import {Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ECRepresentative, EnergyCommunity, EntitySubType, EntityType, SpatialCoordinates} from 'smag-commons/cfg/hierarchy';
import {BehaviorSubject, forkJoin, of, Subject} from 'rxjs';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {I18NService} from '../../i18n/i18n.service';
import {SmagApiService} from '../../api/smag-api.service';
import {catchError, first, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {validateNotBlank} from '../../commons/validators';
import {
    ENTITY_NAME_FORBIDDEN_CHARACTERS,
    ENTITY_NAME_MAX_LENGTH,
    ENTITY_NAME_VALIDATION_PATTERN,
    entityNameError
} from '../../shared/entity-names';
import {can} from '../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {AddressExt} from '../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {getZone} from '../../api/google-api.service';
import {EnergyCommunityAddress, NewEnergyCommunityRequest} from 'smag-commons/cfg/hierarchy/ec';
import {DialogService} from '../../shared/dialog/dialog.service';
import {ECAUCContractualModeConfig} from 'smag-commons/dto/api/customer';

export interface NewCommunityRequest {
    community: NewEnergyCommunityRequest;
    representative?: ECRepresentative;
}


interface RootNodeInfo {
    id: string; // `null` means "true" root
    name: string;
    children: HierarchyEntity[];
}


@Component({
    selector: 'app-ec-creator',
    templateUrl: './ec-creator.component.html',
    styleUrls: ['./ec-creator.component.scss']
})
export class EcCreatorComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private selectedAddress: AddressExt = null;
    private selectedMunicipalityCoordinates: SpatialCoordinates = null;
    private representatives: ECRepresentative[] = [];

    communityForm: FormGroup;
    representativeForm: FormGroup;
    rootNodes: RootNodeInfo[] = [];
    filteredRepresentatives: Subject<ECRepresentative[]> = new BehaviorSubject([]);
    busy = false;
    contractualModes: ECAUCContractualModeConfig[] = [];

    @Output() created: EventEmitter<NewCommunityRequest> = new EventEmitter<NewCommunityRequest>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('selectActivationDate') private selectActivationDateDialog: TemplateRef<any>;

    EntityType = EntityType;
    EntitySubType = EntitySubType;

    constructor(private readonly i18n: I18NService,
                private readonly fb: FormBuilder,
                private apiService: SmagApiService,
                private hierarchyService: HierarchyService,
                private dialogService: DialogService) {
        this.communityForm = this.fb.group({
            rootNode: new FormControl(null, []),
            ecName: new FormControl('',
                [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN),
                    this.validateNameUniqueness.bind(this)], []),
            type: new FormControl(EntityType.ENERGY_COMMUNITY),
            subType: new FormControl(null, [Validators.required]),
            activationDate: new FormControl(null, []),
            contractualMode: new FormControl(null, []),
            address: new FormControl(null, [Validators.required]),
        });

        this.representativeForm = this.fb.group({
            representative: new FormControl(null, [])
        });

        this.representativeFormControl.valueChanges.pipe(
            map(name => (name ? this.filterRepName(name) : this.representatives.slice())),
            startWith(''),
            takeUntil(this.destroyed)
        ).subscribe(this.filteredRepresentatives);
    }

    ngOnInit(): void {
        this.rootNodeControl.valueChanges
            .pipe(
                tap(() => this.communityForm.controls.ecName.updateValueAndValidity()),
                switchMap(rootNode => forkJoin([
                    this.apiService.listRepresentatives(1, 50, null, null, null, null, null, [rootNode.id])
                        .pipe(catchError(() => of({results: [], total: 0}))),
                    this.apiService.getCustomerConfig(rootNode.id)
                ])),
                takeUntil(this.destroyed))
            .subscribe(([result, cfg]) => {
                this.representatives = result.results;
                this.filteredRepresentatives.next(result.results);
                this.contractualModes = cfg.auc_contractual_modes ?? [];
            });

        this.subTypeControl.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                if (this.isContractualModesEnabled) {
                    this.contractualModeControl.setValidators([Validators.required]);
                } else {
                    this.contractualModeControl.clearValidators();
                    this.contractualModeControl.setValue(null);
                }
            });

        this.hierarchyService.hierarchy
            .pipe(first())
            .subscribe(hierarchy => {
                this.rootNodes = [];
                if (can(hierarchy.permissions, Permission.CREATE)) {
                    this.rootNodes.push({
                        id: hierarchy.root,
                        name: this.i18n.translate('None__{root}'),
                        children: hierarchy.entities
                    });
                }
                hierarchy.entities
                    .filter(res => res.type === EntityType.ROOT_NODE && can(res.permissions, Permission.CREATE))
                    .forEach(res => this.rootNodes.push({id: res.id, name: res.name, children: res.children}));
                if (this.rootNodes.length) {
                    this.rootNodeControl.setValue(this.rootNodes[0]);
                }
            });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get nameForbiddenCharacters(): string {
        return ENTITY_NAME_FORBIDDEN_CHARACTERS.join(', ');
    }

    get nameMaxLength(): number {
        return ENTITY_NAME_MAX_LENGTH;
    }

    get nameError(): string {
        return entityNameError(this.communityForm.controls.ecName);
    }

    get addressFormControl(): FormControl {
        return this.communityForm.controls.address as FormControl;
    }

    get activationDateFormControl(): FormControl {
        return this.communityForm.controls.activationDate as FormControl;
    }

    get representativeFormControl(): FormControl {
        return this.representativeForm.controls.representative as FormControl;
    }

    get contractualModeControl(): FormControl {
        return this.communityForm.controls.contractualMode as FormControl;
    }

    get subTypeControl(): FormControl {
        return this.communityForm.controls.subType as FormControl;
    }

    get rootNodeControl(): FormControl {
        return this.communityForm.controls.rootNode as FormControl;
    }

    get isContractualModesEnabled() {
        return this.contractualModes.length > 0 && this.subTypeControl.value === EntitySubType.COLLECTIVE_SELF_CONSUMPTION_GROUP;
    }

    addressChange(event: AddressExt) {
        this.selectedAddress = event;
        if (this.selectedAddress.coordinates) {
            this.selectedMunicipalityCoordinates = this.selectedAddress.coordinates;
        }
    }

    selectActivationDateEvent(activationDate: any, closeFn: () => void): void {
        if (activationDate) {
            this.activationDateFormControl.setValue(activationDate);
        }
        closeFn();
    }

    openActivationDateDialog() {
        this.dialogService.open<any, EnergyCommunity>({
            title: this.i18n.translate('Community activation date', ''),
            template: this.selectActivationDateDialog,
            context: {}
        });
    }

    resetActivationDate() {
        this.activationDateFormControl.setValue(null);
    }

    cancel() {
        this.created.emit(null);
    }

    save(addRepresentative: boolean) {
        this.busy = true;
        const representative = addRepresentative ? this.representativeForm.controls.representative.value : null;
        const activationDate = this.activationDateFormControl.value;
        const community: NewEnergyCommunityRequest = {
            tenant_id: this.rootNodeControl.value?.id,
            name: this.communityForm.controls.ecName.value,
            sub_type: this.subTypeControl.value,
            address: copyAddress(this.selectedAddress),
            spatial_coordinates: this.selectedAddress.coordinates,
            zone: getZone(this.selectedAddress.region),
            activation_date: activationDate ? activationDate.valueOf() : null,
            municipality_coordinates: this.selectedMunicipalityCoordinates,
            contractual_mode: this.contractualModeControl.value
        };
        this.created.emit({community, representative});
    }

    onClickResetRepresentativeSearch() {
        this.representativeFormControl.setValue(null);
    }

    representativeDisplayFn(rep: ECRepresentative): string {
        return rep ? `${rep.personal_data.first_name} ${rep.personal_data.last_name}` : '';
    }

    onRepresentativeLoseFocus(focusEvent: FocusEvent) {
        // That means that the user clicked outside an autocomplete element, so we need to trigger here the fire event
        if (focusEvent.relatedTarget && (focusEvent.relatedTarget as Element).tagName !== 'MAT-OPTION') {
            if (typeof this.representativeFormControl.value !== 'object') {
                // we have lost focus on (autocomplete) input, but not because we have selected an actual option:
                // simulate the dismissal of the constraint form
                this.onClickResetRepresentativeSearch();
            }
        }
    }

    private filterRepName(name: string): ECRepresentative[] {
        if (!!name && name.length > 0) {
            const filterValue = name.toLowerCase();
            return this.representatives
                .filter(option => option.personal_data.first_name.toLowerCase().includes(filterValue) ||
                    option.personal_data.last_name.toLowerCase().includes(filterValue));
        }
        return this.representatives;
    }


    private validateNameUniqueness(ctrl: AbstractControl): ValidationErrors {
        const name = ctrl.value ? ctrl.value.trim() : null;
        if (!name) {
            return null;
        }
        const rootNode: RootNodeInfo = this.rootNodeControl.value;
        if (!rootNode) { // safety check - should not happen
            return null;
        }
        const found = !!rootNode.children.find(sibling => sibling.name === name);
        return found ? {duplicate: true} : null;
    }
}

export function copyAddress(from: AddressExt): EnergyCommunityAddress {
    return {
        street: from.street,
        street_number: from.streetNumber,
        locality: from.locality,
        postal_code: from.postalCode,
        municipality: from.municipality,
        province: from.province,
    };
}
